import { Button } from '@cokitchen/cokitchen-components';
import UpdateMealCard from 'components/base/update-meal-card';
import { useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import OrderDetailsUser from '../order-details-info/order-details-user';
import { LocationIcon } from 'cokitchen-icons';
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrow-left.svg';
import { ReactComponent as RestaurantIcon } from 'assets/icons/restaurants.svg';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CatetgoryIcon } from 'assets/icons/category.svg';
import { ReactComponent as AddonIcon } from 'assets/icons/addons.svg';
import useDashboardContext from 'contexts/dashboard.context';
import { UpdateOrderBrandMeal } from 'models/order.model';
import { OrderSingleMealEditType } from 'models/meal.model';
import { useOrderData, useRestaurantMeals, useOrderManagement, useEditMealAddons } from './hooks';
import MealCategoryCard from './meal-category-card';
import BrandCard from './brand-card';
import { isBrandOpenNow } from './functions';
import AddonCategoryCard from './addon-category-card';
import { OrderAddonType } from 'models/addon.model';
import { formatNumber, handleApiError } from 'utils/functions';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import ConfirmUndoModal from './confirm-undo-modal';

const UpdateOrderDetails = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const { activeCokitchen } = useDashboardContext();
  const [newMealDetails, setNewMealDetails] = useState<UpdateOrderBrandMeal | null>(null);
  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [showUndoModal, setShowUndoModal] = useState(false);

  const {
    order,
    loading,
    getOrder,
    subTotal,
    updateOrder,
    updating,
    updatedOrderDetails,
    isOrderUpdated,
    isProcessingOrder
  } = useOrderData(order_id);
  const {
    step,
    goToStep,
    brands,
    mealLoading,
    getBrands,
    loadRestaurant,
    restaurant,
    selectedMeal,
    loadMeal
  } = useRestaurantMeals();

  const {
    orderMeals,
    decrementPack,
    incrementPack,
    handleDeleteMeal,
    headerDetails,
    setOrderMeals,
    handleUndoChanges,
    hasOrderChanged,
    newTotal
  } = useOrderManagement(order);

  const { editingMeal, setEditingMeal, incrementAddon, replaceAddon, deleteAddon } =
    useEditMealAddons({
      setOrderMeals,
      brandId: editingBrandId ?? newMealDetails?.id
    });

  const isBrandOpen = useMemo(() => {
    if (!restaurant) return false;
    return isBrandOpenNow(restaurant);
  }, [restaurant]);

  const addonCategories = useMemo(() => {
    if (!selectedMeal) return [];
    return selectedMeal.addon_categories.map((cat) => {
      const addons = cat.addons.map((a) => {
        const found = editingMeal?.addons.find((_a) => _a.id === a.id);

        return {
          ...a,
          quantity: Number(found?.quantity ?? 0) ?? 0
        };
      });
      return { ...cat, addons };
    });
  }, [selectedMeal, editingMeal]);

  const handleRestaurantButtonClick = () => {
    if (brands.length > 0) {
      setEditingBrandId(null);
      setEditingMeal(null);
      goToStep(1);
    } else {
      getBrands(activeCokitchen);
      goToStep(1);
    }
  };

  const handleEditMeal = async (meal: OrderSingleMealEditType, brandId: string) => {
    try {
      setEditingMeal(meal);
      setEditingBrandId(brandId);

      await loadMeal(meal.id);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleConfirmOrder = async () => {
    if (!isOrderUpdated) {
      const success = await updateOrder(orderMeals);
      if (success) {
        toast(<ToastMessage type='success' message='Order updated successfully!' />);
      }
    }
    if (isOrderUpdated && order) {
      navigate(-1);
    }
  };

  useEffect(() => {
    getOrder();
  }, []);

  useEffect(() => {
    if (!selectedMeal || !restaurant) return;

    if (editingMeal && editingMeal.id === selectedMeal.id) return;

    const defaultAddons = selectedMeal.addon_categories.reduce((addons, cat) => {
      for (const addon of cat.addons) {
        if (addon.default) addons.push({ ...addon, quantity: 1 });
      }
      return addons;
    }, [] as OrderAddonType[]);

    const newMeal = {
      id: selectedMeal.id,
      name: selectedMeal.name,
      quantity: 1,
      amount: selectedMeal.amount,
      order_note: '',
      addons: [...defaultAddons]
    };

    setEditingMeal(newMeal);
    setEditingBrandId(restaurant.id);
    const oldMealOrder = orderMeals.find((order) => order.id === restaurant.id);

    if (oldMealOrder) {
      if (oldMealOrder.meals.find((m) => m.id === newMeal.id)) return;

      setOrderMeals((prev) =>
        prev.map((order) =>
          order.id === restaurant.id ? { ...order, meals: [...order.meals, newMeal] } : order
        )
      );
      return;
    }

    const newMealOrder = {
      id: restaurant.id,
      brandName: restaurant.name,
      amount: 0,
      quantity: 1,
      meals: [newMeal]
    };
    setNewMealDetails(newMealOrder);
    setOrderMeals((prev) => [newMealOrder, ...prev]);
  }, [selectedMeal]);

  if (loading)
    return (
      <div className='flex mt-60 item-center justify-center'>
        <p className='font-bold'>Loading...</p>
      </div>
    );

  return (
    <div className='overflow-y-auto h-full'>
      <div className='flex gap-2 items-center mb-2'>
        <button
          onClick={() => navigate(-1)}
          aria-label='Go back to previous page'
          className='p-2 hover:bg-gray-1 rounded transition-colors'
          type='button'>
          <ArrowLeftIcon className='w-6 h-6' aria-hidden='true' />
        </button>
        <h2 className='font-medium text-[#1A1A1A]'>Order #{order_id}</h2>
      </div>
      <div className='flex max-h-[calc(100vh-180px)]  overflow-y-auto flex-col md:flex-row border-t border-l border-[#DCE2EA]'>
        <main className='basis-4/6 overflow-auto'>
          <div className='p-4'>{order && <OrderDetailsUser order={order} />}</div>

          <div className='border-t p-4 border-[#DCE2EA]'>
            <div className='flex gap-2  mb-2 '>
              <button
                onClick={handleRestaurantButtonClick}
                className={`${
                  step === 1 ? 'text-[#F73B58] border-[#F73B58]' : ' border-[#EAECEC]  '
                } ${
                  !isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''
                } flex bg-gray-11 items-center gap-2 border text-nowrap rounded-[40px] p-2`}>
                <span className='border border-[#EAECEC] rounded-full p-1'>
                  <RestaurantIcon />
                </span>
                Restaurants
              </button>

              <div className='flex gap-2 overflow-x-auto scrollbar-none border-l border-gray-3 px-3'>
                <button
                  onClick={() => goToStep(2)}
                  disabled={!restaurant || restaurant.meal_categories.length === 0}
                  className={`${
                    step === 2 ? 'text-[#F73B58] border-[#F73B58]' : ' border-[#EAECEC]  '
                  } ${
                    !restaurant || restaurant.meal_categories.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : ' '
                  } bg-gray-11 flex items-center gap-2  border text-nowrap rounded-[40px] p-2`}>
                  <span
                    className={`${
                      step === 2 ? ' border-[#F73B58] text-[#F73B58]' : ' border-[#EAECEC]'
                    } border rounded-full p-1`}>
                    <CatetgoryIcon />
                  </span>
                  Main dish
                </button>

                {addonCategories.length > 0 &&
                  addonCategories.map((category) => (
                    <button
                      key={category.id}
                      className=' bg-gray-11 flex items-center gap-2 text-gray-5 border text-nowrap border-[#EAECEC]  rounded-[40px] p-2'>
                      <span className='border border-[#EAECEC]  rounded-full p-1'>
                        <AddonIcon />
                      </span>
                      {category.name}
                    </button>
                  ))}
              </div>
            </div>

            {mealLoading ? (
              <div className='font-bold mt-12 text-center'>Loading...</div>
            ) : brands.length === 0 && addonCategories.length === 0 ? (
              isProcessingOrder ? (
                <p className='text-center w-full mt-24'>Please select a restaurant</p>
              ) : (
                <p className='text-center w-full mt-24'>Sorry, this Order can not be edited</p>
              )
            ) : (
              <>
                {step === 1 && (
                  <div className='grid grid-cols-3 gap-4'>
                    {brands.map((brand) => (
                      <BrandCard
                        key={brand.id}
                        brand={brand}
                        onClick={() => loadRestaurant(brand.id)}
                      />
                    ))}
                  </div>
                )}

                {step === 2 && (
                  <div>
                    {restaurant &&
                      restaurant.meal_categories.map((category) => (
                        <div key={category.id} className='mb-6 '>
                          <h2 className='font-semibold mb-2 bg-gray-11 p-2 text-[#1A1A1A]'>
                            {category.name}
                          </h2>

                          <div className='grid grid-cols-3 gap-4'>
                            {category.meals.map((meal) => (
                              <MealCategoryCard
                                key={meal.id}
                                meal={meal}
                                isBrandOpen={isBrandOpen}
                                onClick={() => loadMeal(meal.id)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {step === 3 && (
                  <div>
                    {addonCategories.length > 0 ? (
                      <ul className='flex flex-col items-start gap-7 px-0 md:px-2 py-5 sm:px-5'>
                        {addonCategories.map((category) => (
                          <AddonCategoryCard
                            key={category.id}
                            addonCategory={category}
                            onAdd={incrementAddon}
                            onReplace={replaceAddon}
                            onDelete={deleteAddon}
                          />
                        ))}
                      </ul>
                    ) : (
                      <div>
                        <p className='text-center w-full mt-24'>No Addons Available</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>

        {/* Second half */}

        <section className='bg-[#F7F7F7] p-2 max-h-full overflow-y-auto flex flex-col gap-2  basis-2/6 '>
          <div className='p-4 bg-white'>
            <div className='flex justify-between items-center '>
              <h6 className='font-medium text-base text-[#1A1A1A]'>Basket</h6>
              <button
                onClick={() => setShowUndoModal(true)}
                className={`${
                  !isProcessingOrder ? 'opacity-50 cursor-not-allowed' : ''
                } text-xs text-red-4 hover:text-red-5`}>
                Undo changes
              </button>
            </div>
            <p className='text-xs text-gray-8'>
              {headerDetails.brandsTotal} brands, {headerDetails.mealsTotal} items
            </p>
            <div className='mt-6 flex items-center gap-2'>
              <LocationIcon color='gray' />{' '}
              <p className='truncate text-sm text-[#595959]'>
                {order?.calculated_order.address_details.address_line}
              </p>
            </div>
          </div>
          {/* Meals details */}
          <div className='flex flex-col gap-2'>
            {orderMeals.map((order) => (
              <UpdateMealCard
                key={order.id}
                brandMeal={order}
                onDeleteMeal={handleDeleteMeal}
                onDecrement={decrementPack}
                onIncrement={incrementPack}
                onEditMeal={(meal) => handleEditMeal(meal, order.id)}
              />
            ))}
          </div>

          {!isOrderUpdated ? (
            <div className='flex bg-white flex-col p-4 gap-2'>
              <div className='flex justify-between font-medium text-[#1A1A1A] '>
                <h3>SubTotal</h3>
                <p>{formatNumber(subTotal)}</p>
              </div>
              {hasOrderChanged && (
                <div className='flex justify-between font-medium text-[#1A1A1A] '>
                  <h3> New SubTotal</h3>
                  <p>{formatNumber(newTotal)}</p>
                </div>
              )}
            </div>
          ) : (
            updatedOrderDetails && (
              <div className='bg-white p-4'>
                <div className='flex bg-[#F7F7F7] flex-col gap-1 rounded-md'>
                  <div className='flex justify-between px-3 pt-2 text-sm font-medium text-[#A3A3A3] '>
                    <h3>SubTotal</h3>
                    <div className='flex items-center gap-1'>
                      <p>{formatNumber(updatedOrderDetails.subTotal)}</p>
                      <p className='border border-blue-3 text-blue-3 text-xs p-1 rounded-md '>
                        <span>
                          {updatedOrderDetails.subTotal - Number(subTotal) > 0 ? '+' : '-'}
                        </span>
                        {formatNumber(updatedOrderDetails.subTotal - Number(subTotal))}
                      </p>
                    </div>
                  </div>
                  <div className='flex justify-between px-3 pt-2 text-sm font-medium text-[#A3A3A3] '>
                    <h3>Delivery fee</h3>
                    <p>{formatNumber(updatedOrderDetails.deliveryFee)}</p>
                  </div>
                  <div className='flex justify-between px-3 pt-2 text-sm font-medium text-[#A3A3A3] '>
                    <h3>Service fee</h3>
                    <p>{formatNumber(updatedOrderDetails.serviceCharge)}</p>
                  </div>

                  {/* Total */}
                  <div className='flex bg-[#F0F0F0] p-3 mt-2 text-sm justify-between font-medium text-[#1A1A1A] '>
                    <h3>Total</h3>
                    <p>{formatNumber(updatedOrderDetails.total)}</p>
                  </div>
                </div>
              </div>
            )
          )}
          <div className='flex gap-2 p-4 bg-white'>
            {!isOrderUpdated && (
              <Button
                className='text-3 md:text-sm grow w-full md:max-w-[270px]  lg:max-w-[500px]'
                color='gray'
                variant='block'
                onClick={() => navigate(-1)}>
                Cancel
              </Button>
            )}
            <Button
              className='text-3 md:text-sm grow w-full md:max-w-[270px]  lg:max-w-[500px]'
              color='primary'
              variant='block'
              disabled={!hasOrderChanged || updating}
              onClick={handleConfirmOrder}>
              {updating ? 'Updating...' : isOrderUpdated ? 'View Order' : 'Confirm Order'}
            </Button>
          </div>
        </section>
      </div>
      <ConfirmUndoModal
        showUndoModal={showUndoModal}
        onClose={() => setShowUndoModal(false)}
        onConfirm={() => {
          handleUndoChanges();
          setShowUndoModal(false);
        }}
      />
    </div>
  );
};

export default UpdateOrderDetails;
